import React from 'react';
import {ButtonDropdownField, DropdownList} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';

export interface ConditionOperatorSelectProps {
	options: {value: string; label: string}[];
	value: string;
	onChange: (operator: string) => void;
	/** Index of the condition, so each tile's control and options carry their own `data-cy`. */
	index: number;
}

/** The operator choice for a company custom field, as a dropdown rather than a segmented control.
 *
 * A custom field declares up to six comparisons, and the longest labels ("Greater than or equal",
 * "Does not contain") do not fit one line when six equal segments share the condition popover's
 * fixed width. A menu takes one row whatever the number of operators, so the popover stays at its
 * standard width. Renders nothing for a single option, like the segmented control it stands in for.
 *
 * The menu is portalled so the popover's own scrolling does not clip it. Picking an option closes
 * only this menu; the outer popover stays open so the value can be entered next. */
export function ConditionOperatorSelect({options, value, onChange, index}: ConditionOperatorSelectProps): JSX.Element | null {
	const {formatMessage} = useIntl();

	if (options.length < 2) {
		return null;
	}

	const selected = options.find(option => option.value === value);
	const label = formatMessage({id: 'ai_report_builder.conditions.operator_label', defaultMessage: 'Condition'});

	return (
		<DropdownList>
			<DropdownList.Trigger>
				<ButtonDropdownField
					width={'100%'}
					aria-label={selected ? `${label} ${selected.label}` : label}
					data-cy={`ai-report-condition-operator-select-${index}`}
				>
					{selected?.label ?? ''}
				</ButtonDropdownField>
			</DropdownList.Trigger>
			<DropdownList.Content offset={4} align={'start'} useTriggerWidth portalled>
				{options.map(option => (
					<DropdownList.Item
						key={option.value}
						onClick={() => onChange(option.value)}
						data-cy={`ai-report-condition-operator-select-${index}-${option.value}`}
					>
						{option.label}
					</DropdownList.Item>
				))}
			</DropdownList.Content>
		</DropdownList>
	);
}
